import { useTranslation } from 'react-i18next';
import { DAY_ROUTE_COLORS } from '../../hooks/useAllDayRoutes';
import type { ItineraryDay } from '../../types';
import { Icon, MapPin, Bookmark, CircleDashed } from '../ui/Icon';
import type { ReactNode } from 'react';
import MapFilterBar from './MapFilterBar';

interface Props {
  /** Chips before the rest — the destination chips (`LegChips`). */
  leading?: ReactNode;
  days: ItineraryDay[];
  enabledDays: Set<number>;
  onToggleDay: (dayIdx: number) => void;
  onToggleAll: (dayIdxs: number[]) => void;
  /** Days whose route is already drawn. The rest show stops without a line. */
  daysWithRoute: Set<number>;
  /** Optional stops on the map — on by default, hiding them is the choice. */
  showOptional: boolean;
  onToggleOptional: () => void;
  optionalCount: number;
  /** Saved places that aren't a stop on any route — off by default. */
  showSavedPlaces: boolean;
  onToggleSavedPlaces: () => void;
  savedPlacesCount: number;
}

export default function DayRouteFilterBar({
  leading, days, enabledDays, onToggleDay, onToggleAll, daysWithRoute,
  showOptional, onToggleOptional, optionalCount,
  showSavedPlaces, onToggleSavedPlaces, savedPlacesCount,
}: Props) {
  const { t } = useTranslation();

  const routedDays = days
    .map((day, i) => ({ day, i }))
    .filter(({ day }) => day.activities.length > 0);
  if (routedDays.length === 0) return null;

  const allEnabled = routedDays.every(({ i }) => enabledDays.has(i));

  return (
    <MapFilterBar>
      {leading}
      <button
        onClick={() => onToggleAll(routedDays.map(({ i }) => i))}
        title={allEnabled ? t('map.hideAll') : t('map.showAllDays')}
        className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium border transition-all cursor-pointer shrink-0 ${
          allEnabled
            ? 'border-transparent bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900'
            : 'text-slate-400 dark:text-slate-500 border-slate-200 dark:border-slate-700 opacity-50'
        }`}
      >
        <Icon as={MapPin} size={12} />
        {t('venues.filter.all')}
      </button>

      {routedDays.map(({ day, i }) => {
        const color = DAY_ROUTE_COLORS[i % DAY_ROUTE_COLORS.length];
        const enabled = enabledDays.has(i);
        // On but without a line: nobody has opened this day yet, so only its
        // stops are on the map. The hollow dot says it before the user wonders.
        const awaitingRoute = enabled && !daysWithRoute.has(i);
        return (
          <button
            key={day.id}
            onClick={() => onToggleDay(i)}
            title={awaitingRoute ? t('map.dayNotCalculated') : undefined}
            className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium border transition-all cursor-pointer shrink-0 ${
              enabled
                ? 'text-slate-800 dark:text-slate-200 border-transparent'
                : 'text-slate-400 dark:text-slate-500 border-slate-200 dark:border-slate-700 opacity-50'
            }`}
            style={enabled ? { backgroundColor: color + '22', borderColor: color + '66' } : {}}
          >
            <span
              className="w-2.5 h-2.5 rounded-full shrink-0"
              style={
                awaitingRoute
                  ? { border: `2px solid ${color}`, backgroundColor: 'transparent' }
                  : { backgroundColor: enabled ? color : '#94a3b8' }
              }
            />
            {t('map.dayLabel', { n: day.dayNumber })}{day.title ? ` · ${day.title}` : ''}
          </button>
        );
      })}

      {optionalCount > 0 && (
        <>
          <span className="w-px h-5 bg-slate-200 dark:bg-slate-700 shrink-0" aria-hidden />
          <button
            onClick={onToggleOptional}
            title={t('map.optionalStopsHint')}
            aria-pressed={showOptional}
            className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium border transition-all cursor-pointer shrink-0 ${
              showOptional
                ? 'border-transparent bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900'
                : 'text-slate-400 dark:text-slate-500 border-slate-200 dark:border-slate-700 opacity-50'
            }`}
          >
            <Icon as={CircleDashed} size={12} />
            {t('map.optionalStops')}
            <span className="tabular-nums opacity-70">{optionalCount}</span>
          </button>
        </>
      )}

      {savedPlacesCount > 0 && (
        <>
          <span className="w-px h-5 bg-slate-200 dark:bg-slate-700 shrink-0" aria-hidden />
          <button
            onClick={onToggleSavedPlaces}
            title={t('map.savedPlacesHint')}
            className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium border transition-all cursor-pointer shrink-0 ${
              showSavedPlaces
                ? 'border-transparent bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900'
                : 'text-slate-400 dark:text-slate-500 border-slate-200 dark:border-slate-700 opacity-50'
            }`}
          >
            <Icon as={Bookmark} size={12} />
            {t('map.savedPlaces')}
            <span className="tabular-nums opacity-70">{savedPlacesCount}</span>
          </button>
        </>
      )}
    </MapFilterBar>
  );
}
